/**
 * ProgressEntryForm Component
 *
 * Form for adding progress entries to POP.
 * Story 6-5
 */

import { useState } from 'react';

interface ProgressEntryFormProps {
  onSubmit: (content: string, progressDate?: string) => void;
  isSubmitting?: boolean;
}

export function ProgressEntryForm({ onSubmit, isSubmitting }: ProgressEntryFormProps) {
  const [content, setContent] = useState('');
  const [progressDate, setProgressDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;

    onSubmit(content, progressDate);
    setContent('');
    setProgressDate(new Date().toISOString().split('T')[0]);
    setIsExpanded(false);
  };

  if (!isExpanded) {
    return (
      <button
        data-testid="add-progress-button"
        onClick={() => setIsExpanded(true)}
        className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg
          text-sm text-gray-600 hover:border-gray-400 hover:text-gray-900
          transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
        Voortgang toevoegen
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Voortgang
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Beschrijf je voortgang, behaalde mijlpalen, geleerde lessen..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Datum</label>
          <input
            type="date"
            value={progressDate}
            onChange={(e) => setProgressDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={() => {
              setContent('');
              setIsExpanded(false);
            }}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            type="submit"
            data-testid="save-progress-button"
            disabled={!content.trim() || isSubmitting}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg
              hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Toevoegen...' : 'Toevoegen'}
          </button>
        </div>
      </div>
    </form>
  );
}
